import React, { useCallback, useEffect, useRef, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { fromCoordinatesToCoordinatesV2 } from '@yme/components/DealerLocatorPage/helpers';
import { usePrevious } from '@yme/hooks/usePrevious';
import { useSelector } from '@yme/hooks/useSelector';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { getModalData } from '@yme/modals/selectors';
import { areMarkersChanged, fitMapToMarkers } from '@yme/molecules/map/helpers';
import { getRTL, getIsoCountryCode } from '@yme/selectors/common';
import { addDistanceToDealers } from '@yme/utils/dealer';
import { MAX_CLOSEST_DEALERS } from './constants';
import { MapModalDesktop } from './desktop/MapModalDesktop';
import { MapModalMobile } from './mobile/MapModalMobile';
export function MapModal() {
    const isRightToLeft = useSelector(getRTL);
    const { googleApiKey, myGeolocation, inputLocation, selectedDealerId: preSelectedDealerId, dealers, setInputLocation, onDealerSubmit, isHiddenCloseIcon, } = useSelector(getModalData);
    const isoCountryCode = useSelector(getIsoCountryCode);
    const [isMapInitialFitDone, setIsMapInitialFitDone] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(inputLocation);
    const mapInstanceRef = useRef(null);
    const prevSelectedLocation = usePrevious(selectedLocation);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const prevDealers = usePrevious(dealers);
    const { isInfiniteDesktopView: isDesktop } = useWindowSize();
    const [selectedDealerId, setSelectedDealerId] = useState(preSelectedDealerId || null);
    useEffect(() => {
        setTimeout(() => {
            setIsMapLoaded(true);
        }, 0);
    }, []);
    const handleMapLoaded = useCallback((mapInstance) => {
        mapInstanceRef.current = mapInstance;
    }, []);
    const handleMapInfoWindowDismiss = useCallback(() => {
        onDealerSubmit(null);
    }, [onDealerSubmit]);
    const submitDealer = useCallback((vicinityId) => {
        onDealerSubmit(vicinityId);
    }, [onDealerSubmit]);
    const handleInputLocation = useCallback((location) => {
        setSelectedLocation(location);
        setInputLocation(location);
    }, [setInputLocation]);
    useEffect(() => {
        if (!mapInstanceRef.current || !isMapLoaded) {
            return;
        }
        let markersToFit = [];
        if (!isMapInitialFitDone || selectedLocation !== prevSelectedLocation) {
            if (selectedLocation) {
                const dealersWithDistance = addDistanceToDealers(dealers, selectedLocation?.coordinates);
                const closestDealers = dealersWithDistance
                    .slice()
                    .sort((a, b) => a.distance - b.distance)
                    .slice(0, MAX_CLOSEST_DEALERS);
                markersToFit = [
                    fromCoordinatesToCoordinatesV2(selectedLocation.coordinates),
                    ...closestDealers,
                ];
            }
            else {
                markersToFit = dealers;
            }
            setIsMapInitialFitDone(true);
        }
        else if (areMarkersChanged(dealers, prevDealers)) {
            markersToFit = dealers;
        }
        if (!isEmpty(markersToFit)) {
            fitMapToMarkers(mapInstanceRef.current, markersToFit);
        }
    }, [
        dealers,
        isMapInitialFitDone,
        isMapLoaded,
        prevDealers,
        prevSelectedLocation,
        selectedLocation,
    ]);
    const MapModalComponent = isDesktop ? MapModalDesktop : MapModalMobile;
    return (React.createElement(MapModalComponent, { country: isoCountryCode, googleApiKey: googleApiKey, dealers: dealers, selectedDealerId: selectedDealerId, preselectedDealerId: undefined, myLocation: myGeolocation, inputLocation: inputLocation, isRightToLeft: isRightToLeft, handleMapInfoWindowDismiss: handleMapInfoWindowDismiss, submitDealer: submitDealer, setSelectedDealerId: setSelectedDealerId, setInputLocation: handleInputLocation, handleMapLoaded: handleMapLoaded, isHiddenCloseIcon: isHiddenCloseIcon }));
}
